/**
 * Weidner Via6 ReviewsSlider section.
 *
 * Mirrors the "See what residents are saying" testimonials block on
 * via6living.com — a heading column on the left (1/3) and a single-
 * card review slider on the right (2/3). One review is visible at a
 * time; visitors paginate manually via prev/next arrows positioned on
 * either side of the card. There is no auto-advance — the slider only
 * moves when the visitor clicks an arrow.
 *
 * Why a collection binding (not unrolled slot groups):
 *   - Reviews are a true variable-length array — properties have wildly
 *     different review counts, so the per-card data has to come from
 *     `useCollectionBinding('reviewsslider.items', { collection: 'reviews' })`.
 *   - The heading is an independently editable richtext token.
 *
 * Card layout: review text first (large quote-style body) with the
 * star rating centered directly below. Author/date meta is
 * intentionally omitted — the quote and the rating are the only
 * content on the card. Everything is center-aligned. The card itself
 * is borderless and shares the section background (primary), so the
 * quote reads as floating in the colored field rather than enclosed
 * in a chip.
 *
 * If the property has no reviews, the section behaves differently
 * depending on context:
 *   - In edit mode: we render a small set of generic placeholder
 *     cards (no claims attributed to real people) so the slider still
 *     has visible structure in the editor preview and an editor knows
 *     where to add real reviews.
 *   - On the live site: the entire section is hidden — an empty
 *     "what residents are saying" slider with placeholder copy reads
 *     as broken to a real visitor.
 *
 * Tokenized via `--vibe-color-*` and Tailwind font tokens; no
 * hard-coded brand colors.
 */
import { useMemo, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useEditMode,
  RichTextContent,
} from '@vibesites/runtime/react';

// Number of placeholder cards rendered when the property's `reviews`
// collection is empty. Kept generic so we never put words in the mouth
// of a real resident.
const PLACEHOLDER_COUNT = 3;

interface Review {
  name?: string | null;
  title?: string | null;
  text?: string | null;
  rating?: string | null;
  date?: string | null;
}

// Coerce the (string-valued) rating field to a 0-5 star count and
// render the star row. The rating field is stored as a string in the
// `reviews` collection (per reference/collections.md), so we parse and
// clamp before drawing. Returns null when the value isn't a usable
// number so we can omit the row entirely.
function StarRating({ rating }: { rating?: string | null }) {
  const parsed = rating ? parseFloat(rating) : NaN;
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  const stars = Math.max(0, Math.min(5, Math.round(parsed)));
  return (
    <div
      className="flex justify-center gap-0.5 text-[var(--vibe-color-warning)]"
      aria-label={`${stars} out of 5 stars`}
    >
      {Array.from({ length: 5 }).map((_, i) => (
        <svg
          key={i}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
          className={`h-5 w-5 ${i < stars ? 'opacity-100' : 'opacity-25'}`}
        >
          <path d="M10 1.5l2.6 5.27 5.82.85-4.21 4.1.99 5.78L10 14.77l-5.2 2.73.99-5.78L1.58 7.62l5.82-.85L10 1.5z" />
        </svg>
      ))}
    </div>
  );
}

interface ReviewCardProps {
  review: Review;
}

function ReviewCard({ review }: ReviewCardProps) {
  const text =
    review.text?.trim() ||
    'A great place to call home.';

  return (
    <article className="h-full flex flex-col items-center justify-center text-center bg-[var(--vibe-color-primary)] text-[var(--vibe-color-text-on-primary)] px-6 py-10 sm:px-12 sm:py-14">
      {/* Review body — leads the card so the visitor's eye starts on
          the quote. Author/date meta is intentionally omitted per
          the design — only the quote and the rating appear. */}
      <div
        className="font-body text-base sm:text-lg leading-relaxed max-w-2xl [&_p]:m-0 [&_p+p]:mt-3"
        // Reviews `text` is a richtext field in the collection schema —
        // render as HTML so editor-authored emphasis carries through.
        dangerouslySetInnerHTML={{ __html: text }}
      />

      {/* Star rating — centered, sits directly under the review text. */}
      <div className="mt-6">
        <StarRating rating={review.rating} />
      </div>
    </article>
  );
}

interface ArrowButtonProps {
  direction: 'prev' | 'next';
  onClick: () => void;
  disabled?: boolean;
}

/**
 * Solid circular chevron button positioned on the side of the slider.
 * Filled with the secondary brand color, icon stroked in
 * text-on-primary so the contrast holds against the primary section
 * background. Sized generously so it works as a tap target on touch
 * devices. `disabled` is forwarded so the parent can gray out the
 * button when there's nothing to paginate to (one-review case).
 */
function ArrowButton({ direction, onClick, disabled }: ArrowButtonProps) {
  const label = direction === 'prev' ? 'Previous review' : 'Next review';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="inline-flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-full bg-[var(--vibe-color-secondary)] text-[var(--vibe-color-text-on-primary)] transition-opacity hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-5 w-5"
        aria-hidden="true"
      >
        {direction === 'prev' ? (
          <polyline points="15 6 9 12 15 18" />
        ) : (
          <polyline points="9 6 15 12 9 18" />
        )}
      </svg>
    </button>
  );
}

export function ReviewsSlider() {
  const reviews = useCollectionBinding<Review>('reviewsslider.items', {
    collection: 'reviews',
    defaultLimit: 12,
  });

  // Edit-mode awareness: when the property has no reviews we want
  // editors to still see the section (with placeholder copy that tells
  // them how to populate it), but visitors on the live site should
  // never see an empty "what residents are saying" slider. `useEditMode`
  // returns null when this theme runs outside the preview frame, so
  // production renders default to "not editing".
  const editMode = useEditMode();
  const isEditMode = editMode?.isEditMode ?? false;

  // Generic fallback so the slider has visible content in the editor
  // preview even on properties that haven't populated `reviews`. These
  // are intentionally bland — no names of real people, no specific
  // claims — so they read as neutral placeholders rather than fake
  // testimonials when a property forgets to load real review data.
  const placeholderReviews = useMemo<Review[]>(
    () =>
      Array.from({ length: PLACEHOLDER_COUNT }, () => ({
        name: 'Resident',
        text: '<p>Add property reviews in the dashboard to populate this slider.</p>',
        rating: '5',
      })),
    [],
  );

  const hasReviews = reviews.length > 0;
  const items: Review[] = hasReviews ? reviews : placeholderReviews;
  const itemCount = items.length;

  // Discrete pagination — no auto-advance. Index wraps modulo
  // itemCount so the slider loops on prev/next click even at the
  // boundaries. Stored as an unbounded signed integer and normalized
  // at render time so the modulo handles negatives cleanly.
  //
  // NB: `useState` must run on every render path — toggling edit mode
  // changes whether we early-return below, and React forbids changing
  // the number of hooks called between renders. Keep all hook calls
  // above the conditional return.
  const [index, setIndex] = useState(0);
  const safeIndex = itemCount > 0 ? ((index % itemCount) + itemCount) % itemCount : 0;

  // No reviews + not editing: hide the whole section. An empty
  // testimonials block with placeholder copy reads as broken to a
  // real visitor, so we'd rather render nothing at all.
  if (!hasReviews && !isEditMode) {
    return null;
  }

  const goPrev = () => setIndex((i) => i - 1);
  const goNext = () => setIndex((i) => i + 1);

  // Single-item slider has nothing to paginate to — hide the controls
  // entirely (preferred to a permanently-disabled arrow set, which
  // reads as broken).
  const showArrows = itemCount > 1;

  return (
    <section className="py-12 md:py-16 bg-[var(--vibe-color-primary)]">
      <div className="max-w-7xl xl:max-w-[88rem] mx-auto px-4 sm:px-6 lg:px-8">
        {/* 1/3 heading column + 2/3 slider column at >=lg; stacks
            vertically below that. Mirrors the asymmetric layout used
            by PropertyOverview / AmenitiesGrid so the page rhythm
            stays consistent. */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-12 items-center">
          {/* Left 1/3 — heading only */}
          <div className="lg:col-span-1">
            <RichTextContent
              path="reviewsslider.heading"
              as="h2"
              className="font-heading text-4xl sm:text-5xl md:text-6xl font-normal text-[var(--vibe-color-text-on-primary)] leading-tight uppercase [&_strong]:font-normal [&_b]:font-normal"
            />
          </div>

          {/* Right 2/3 — single-card slider with prev/next arrows.
              At sm+ the arrows are absolutely positioned over the
              column's outer edges and vertically centered, so they
              read as siblings of the card rather than overlays on it.
              On mobile the side rails get cramped, so we render a
              centered arrow row directly beneath the card instead. */}
          <div className="lg:col-span-2">
            <div className="relative">
              {/* Card window. `overflow-hidden` clips the off-screen
                  cards as the track translates. We use transform
                  (rather than scrollLeft) so the transition is a
                  single CSS animation and the slider can't get out of
                  sync with `index`.
                  No horizontal inset on mobile — the side arrows are
                  hidden there, so the card can use the full column
                  width. At sm+ we inset to leave room for the side
                  arrows. */}
              <div className="overflow-hidden sm:mx-16">
                <div
                  className="flex transition-transform duration-500 ease-out"
                  style={{ transform: `translateX(-${safeIndex * 100}%)` }}
                  aria-live="polite"
                >
                  {items.map((review, i) => (
                    <div
                      key={`r-${i}`}
                      className="shrink-0 basis-full"
                      aria-hidden={i !== safeIndex}
                    >
                      <ReviewCard review={review} />
                    </div>
                  ))}
                </div>
              </div>

              {showArrows ? (
                <>
                  {/* Side arrows — sm+ only. `inset-y-0` keeps them
                      vertically centered on the card; mobile hides
                      them with `hidden`. */}
                  <div className="hidden sm:flex absolute inset-y-0 left-0 items-center">
                    <ArrowButton direction="prev" onClick={goPrev} />
                  </div>
                  <div className="hidden sm:flex absolute inset-y-0 right-0 items-center">
                    <ArrowButton direction="next" onClick={goNext} />
                  </div>

                  {/* Below-card arrows — mobile only. In normal flow
                      so they push nothing on sm+ (where they're
                      display:none). */}
                  <div className="sm:hidden mt-2 flex justify-center gap-6">
                    <ArrowButton direction="prev" onClick={goPrev} />
                    <ArrowButton direction="next" onClick={goNext} />
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ReviewsSlider',
  displayName: 'Reviews Slider',
  tokens: {
    'reviewsslider.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: 'See what residents are saying',
    },
  },
  collectionBindings: {
    'reviewsslider.items': {
      label: 'Reviews',
      collection: 'reviews',
      defaultLimit: 12,
      description: 'Resident reviews shown one at a time in the slider.',
    },
  },
};

(ReviewsSlider as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default ReviewsSlider;
